import {ApiResponseDto, BasePopupProps, NameWithId} from "@utils/Models";
import FlintLoadingWrapper from "@components/page_components/common/loading/FlintLoadingWrapper";
import {useTranslation} from "react-i18next";
import {WindowActionsBar} from "@progress/kendo-react-dialogs";
import React, {useEffect, useState} from "react";
import FlintButton from "@components/controls/FlintButton";
import FlintDropDownList from "@components/controls/FlintDropDownList";
import axios from "axios";
import {ApiUrl_Members_GetAdminSupervisorList, ApiUrl_Orders_UpdateProjectLeader} from "@utils/Endpoints";
import {
    useFlintNotificationContext
} from "@components/page_components/common/notifications/FlintNotificationContext";
import {useAuthenticationContext} from "@utils/auth/AuthenticationContext";
import {AssignProjectLeaderRequest, OrderPreviewBasicDataModel, OrderPreviewModelStateType} from "../../order.models";
import qs from "qs";

export default function UpdateProjectLeaderPanel(props: UpdateProjectLeaderPanelProps) {
    const {t} = useTranslation();
    const {state, handlers, onlySelfAssignAllowed} = useUpdateProjectLeaderPanel(props);

    return (
        <FlintLoadingWrapper showLoading={state.isLoading.get} loadingWrapperClassName={"d-grid h-100"}>
            <div>
                <div className={"form-group"}>
                    {state.userList.get.length > 0 &&
                     <FlintDropDownList<NameWithId> filterable={true} disabled={onlySelfAssignAllowed} label={t("lblProjectLeader")} 
                                                   dataSource={state.userList.get}
                                                   selectedValue={state.selectedLab.get}
                                                   onItemSelected={handlers.handleUserSelected}/>}
                </div>
            </div>
            <WindowActionsBar layout={"center"}>
                <FlintButton disabled={state.isLoading.get} themeColor={"secondary"}
                             onClick={handlers.handleCancelled}>{t("Cancel")}</FlintButton>
                <FlintButton disabled={state.isLoading.get || !state.selectedLab.get} themeColor={"primary"}
                             onClick={handlers.handleConfirmed}>{t("Confirm")}</FlintButton>
            </WindowActionsBar>
        </FlintLoadingWrapper>
    )
}

export interface UpdateProjectLeaderPanelProps extends BasePopupProps {
    orderPreviewModelState: OrderPreviewModelStateType
}

function useUpdateProjectLeaderPanel(props: UpdateProjectLeaderPanelProps) {
    const [isLoading, setIsLoading] = useState(false);
    const [userList, setUserList] = useState<NameWithId[]>([]);
    const [selectedUser, setSelectedUser] = useState<NameWithId>();
    const {showNotification} = useFlintNotificationContext();
    const {rootModel} = useAuthenticationContext();
    useEffect(() => {
        if (props.orderPreviewModelState.get.Permissions.CanUpdateProjectLeader 
            || props.orderPreviewModelState.get.Permissions.CanUpdateMyselfAsProjectLeader) {
            loadUserList().then();
            return;
        }
        showNotification("You are not allowed to update the project leader", {style:"error"});
        props.OnCancelled();
    }, [])

    function loadUserList() {
        setIsLoading(true);
        const params = {labIds: [props.orderPreviewModelState.get.Info.OrderCurrentLab.Id]}
        return axios.get<ApiResponseDto<NameWithId[]>>(ApiUrl_Members_GetAdminSupervisorList, 
            {
                params: params,
                paramsSerializer: params => qs.stringify(params, { arrayFormat: 'repeat' })
            }).then(res => {
                const currentLeaderId = props.orderPreviewModelState.get.OrderBasicData.ProjectLeader?.Id ?? 0;
                let userList = res.data.Result;
                if (onlySelfAssignAllowed)
                    userList = userList.filter(x => x.Id == rootModel?.CurrentUserProfile.Id);
                else 
                    userList = userList.filter(x => x.Id != currentLeaderId);
                
                setUserList(userList.map((x) => {
                    x.Name = `${x.Name} - (${x.Extension})`;
                    return x;
                }));
                if (onlySelfAssignAllowed)
                    setSelectedUser(userList[0]);
            })
            .catch(err => showNotification(err))
            .finally(() => setIsLoading(false))
    }

    function handleUserSelected(user: NameWithId) {
        setSelectedUser(user);
    }

    function handleCancelled() {
        props.OnCancelled();
    }

    function handleConfirmed() {
        const post : AssignProjectLeaderRequest = {
            OrderGuid : props.orderPreviewModelState.get.OrderBasicData.Guid,
            ProjectLeaderGuid : selectedUser!.Guid
        }
        setIsLoading(true);
        axios.post<ApiResponseDto<OrderPreviewBasicDataModel>>(ApiUrl_Orders_UpdateProjectLeader, post)
            .then(res => {
                props.orderPreviewModelState.set("OrderBasicData", res.data.Result);
                showNotification("Project leader has been successfully updated", {style:"success"});
                props.OnConfirmedAndSaved();
            })
        .catch(err => showNotification(err))
        .finally(() => setIsLoading(false))
    }
    const onlySelfAssignAllowed = !props.orderPreviewModelState.get.Permissions.CanUpdateProjectLeader 
        && props.orderPreviewModelState.get.Permissions.CanUpdateMyselfAsProjectLeader;
    return {
        onlySelfAssignAllowed,
        state: {
            isLoading: {get: isLoading},
            userList: {get: userList},
            selectedLab: {get: selectedUser},
        },
        handlers: {handleConfirmed, handleCancelled, handleUserSelected}
    }
}